<script>
  import {onMount} from 'svelte';
  import {TimelineLite, Power4} from 'gsap/TweenMax';

  // Animation classes:
  let containerBackgroundClass,
    containerTextClass,
    contentPaddingYClass,
    contentPaddingXClass,
    contentPaddingBreakClass,
    headingClass,
    subheadingContainerClass,
    subheadingClass,
    copyClass;
  
  // Cursor classes:
  let containerBackgroundCursor,
    containerTextCursor,
    contentPaddingYCursor,
    contentPaddingXCursor,
    contentPaddingBreakCursor,
    headingCursor,
    subheadingContainerCursor,
    subheadingCursor,
    copyCursor;

  // Animation example:
  let demo, demoContainer, demoContent, demoHeading, demoSubheadingContainer, demoSubheading, demoCopy;

  onMount(() => {
    setTimeout(() => {
      runAnimation();
    }, 5000);
  });

  function tweenTo(el, duration, vars, position) {
    return new Promise(resolve => {
      new TimelineLite().to(
        el,
        duration,
        {
          ...vars,
          onComplete: resolve
        },
        position
      );
    });
  }

  function tweenStaggerTo(el, duration, vars, stagger, position) {
    return new Promise(resolve => {
      new TimelineLite().staggerTo(
        el,
        duration,
        {
          ...vars
        },
        stagger,
        position,
        resolve
      );
    });
  }

  const runAnimation = () => {
    animate(containerBackgroundCursor, containerBackgroundClass, demoContainer, 'bg-sageGreen')
      .then(() => animate(contentPaddingYCursor, contentPaddingYClass, demoContent, 'py-7'))
      .then(() => animate(contentPaddingXCursor, contentPaddingXClass, demoContent, 'px-4'))
      .then(() => animate(contentPaddingBreakCursor, contentPaddingBreakClass, demoContent, 'md:p-7'))
      .then(() => animate(headingCursor, headingClass, demoHeading, 'heading heading-2'))
      .then(() => animate(subheadingContainerCursor, subheadingContainerClass, demoSubheadingContainer, 'my-4'))
      .then(() => animate(subheadingCursor, subheadingClass, demoSubheading, 'heading heading-3'))
      .then(() => animate(copyCursor, copyClass, demoCopy, 'copy'))
      .then(() => animate(containerTextCursor, containerTextClass, [demoContainer, demoCopy], 'text-black'));
  };

  const animate = (cursor, animationClass, demo, style) => {
    return tweenTo(cursor, 0.08, {visibility: 'visible'})
      .then(() => tweenStaggerTo(animationClass.children, 0.08, {display: 'inline-block'}, 0.08))
      .then(() => tweenTo(demo, 1, {className: `+=${style}`, ease: Power4.easeOut}, '+=.25'))
      .then(() => tweenTo(cursor, 0.08, {visibility: 'hidden'}));
  };
</script>

<style>
  .code-green {
    color: hsl(88, 72%, 71%);
  }

  .code-blue {
    color: hsl(193, 100%, 71%);
  }

  .code-red {
    color: hsl(355, 100%, 70%);
  }

  .code-white {
    color: hsl(180, 100%, 96%);
  }

  .code-yellow {
    color: hsl(41, 100%, 67%);
  }

  .code-editor {
    font-family: 'Courier New', Courier, monospace;
    font-weight: bold;
    width: 50rem;
    margin-left: -2rem;
  }

  /* Inline block whitespace correction */
  .inline-block {
    margin-right: -0.5rem;
  }

  .cursor {
    visibility: hidden;
    margin-top: 0.15rem;
  }

  .showcase-content {
    max-width: 100%;
  }

  .showcase-demo {
    margin-left: -15rem;
    margin-right: -2rem;
    width: 50%;
  }
</style>

<template>
  <section class="hidden xl:block page-section bg-sunsetPink40">
    <div class="container">
      <h2 class="heading heading-2 my-6">Workflow demonstration</h2>
      <div>
        <div class="mx-auto w-full showcase-content">
          <div class="flex flex-row justify-center">
            <div class="code-editor text-sm bg-darkest p-6 rounded leading-normal overflow-hidden">
              <div class="whitespace-no-wrap">
                <div class="inline-block code-blue">&lt;</div>
                <div class="inline-block code-red">div&nbsp;</div>
                <div class="inline-block code-yellow">class</div>
                <div class="inline-block code-blue">=&quot;</div>
                <div class="inline-block code-green">flex items-end max-w-full border-box</div>
                <div class="inline-block code-blue">&quot;&gt;</div>
              </div>

              <div class="whitespace-no-wrap">
                <div class="inline-block code-blue">&nbsp;&lt;</div>
                <div class="inline-block code-red">div&nbsp;</div>
                <div class="inline-block code-yellow">class</div>
                <div class="inline-block code-blue">=&quot;</div>
                <div class="inline-block code-green">w-full</div>
                <div bind:this={containerBackgroundClass} class="inline-block code-green">
                  <div class="hidden">&nbsp;</div><!--
                  --><div class="hidden">b</div><!--
                  --><div class="hidden">g</div><!--
                  --><div class="hidden">-</div><!--
                  --><div class="hidden">s</div><!--
                  --><div class="hidden">a</div><!--
                  --><div class="hidden">g</div><!--
                  --><div class="hidden">e</div><!--
                  --><div class="hidden">G</div><!--
                  --><div class="hidden">r</div><!--
                  --><div class="hidden">e</div><!--
                  --><div class="hidden">e</div><!--
                  --><div class="hidden">n</div>
                </div>
                <div bind:this={containerBackgroundCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div>
                <div bind:this={containerTextClass} class="inline-block code-green">
                  <div class="hidden">&nbsp;</div><!--
                  --><div class="hidden">t</div><!--
                  --><div class="hidden">e</div><!--
                  --><div class="hidden">x</div><!--
                  --><div class="hidden">t</div><!--
                  --><div class="hidden">-</div><!--
                  --><div class="hidden">b</div><!--
                  --><div class="hidden">l</div><!--
                  --><div class="hidden">a</div><!--
                  --><div class="hidden">c</div><!--
                  --><div class="hidden">k</div>
                </div>
                <div bind:this={containerTextCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div>
                <div class="inline-block code-blue">&quot;&gt;</div>
              </div>

              <div class="whitespace-no-wrap">
                <div class="inline-block code-blue">&nbsp;&nbsp;&lt;</div>
                <div class="inline-block code-red">div</div>
                <div bind:this={contentPaddingYClass} class="inline-block">
                  <div class="hidden">&nbsp;</div><!--
                  --><div class="hidden code-yellow">c</div><!--
                  --><div class="hidden code-yellow">l</div><!--
                  --><div class="hidden code-yellow">a</div><!--
                  --><div class="hidden code-yellow">s</div><!--
                  --><div class="hidden code-yellow">s</div><!--
                  --><div class="hidden code-blue">=</div><!--
                  --><div class="hidden code-blue">&quot;</div><!--
                  --><div class="hidden code-green">p</div><!--
                  --><div class="hidden code-green">y</div><!--
                  --><div class="hidden code-green">-</div><!--
                  --><div class="hidden code-green">7</div><!--
                    --><div bind:this={contentPaddingXClass} class="inline-block code-green"><!--
                      --><div class="hidden code-green">&nbsp;</div><!--
                      --><div class="hidden code-green">p</div><!--
                      --><div class="hidden code-green">x</div><!--
                      --><div class="hidden code-green">-</div><!--
                      --><div class="hidden code-green">4</div><!--
                    --></div>
                    <div bind:this={contentPaddingXCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div><!--
                    --><div bind:this={contentPaddingBreakClass} class="inline-block code-green"><!--
                      --><div class="hidden code-green">&nbsp;</div><!--
                      --><div class="hidden code-green">m</div><!--
                      --><div class="hidden code-green">d</div><!--
                      --><div class="hidden code-green">:</div><!--
                      --><div class="hidden code-green">p</div><!--
                      --><div class="hidden code-green">-</div><!--
                      --><div class="hidden code-green">7</div><!--
                    --></div>
                    <div bind:this={contentPaddingBreakCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div>
                  <div class="hidden code-blue">&quot;</div>
                </div>
                <div bind:this={contentPaddingYCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div>
                <div class="inline-block code-blue">&gt;</div>
              </div>

              <div class="whitespace-no-wrap">
                <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&lt;</div>
                <div class="inline-block code-red">h2</div>
                <div bind:this={headingClass} class="inline-block">
                  <div class="hidden">&nbsp;</div><!--
                  --><div class="hidden code-yellow">c</div><!--
                  --><div class="hidden code-yellow">l</div><!--
                  --><div class="hidden code-yellow">a</div><!--
                  --><div class="hidden code-yellow">s</div><!--
                  --><div class="hidden code-yellow">s</div><!--
                  --><div class="hidden code-blue">=</div><!--
                  --><div class="hidden code-blue">&quot;</div><!--
                  --><div class="hidden code-green">h</div><!--
                  --><div class="hidden code-green">e</div><!--
                  --><div class="hidden code-green">a</div><!--
                  --><div class="hidden code-green">d</div><!--
                  --><div class="hidden code-green">i</div><!--
                  --><div class="hidden code-green">n</div><!--
                  --><div class="hidden code-green">g</div><!--
                  --><div class="hidden code-green">&nbsp;</div><!--
                  --><div class="hidden code-green">h</div><!--
                  --><div class="hidden code-green">e</div><!--
                  --><div class="hidden code-green">a</div><!--
                  --><div class="hidden code-green">d</div><!--
                  --><div class="hidden code-green">i</div><!--
                  --><div class="hidden code-green">n</div><!--
                  --><div class="hidden code-green">g</div><!--
                  --><div class="hidden code-green">-</div><!--
                  --><div class="hidden code-green">2</div><!--
                  --><div class="hidden code-blue">&quot;</div>
                </div>
                <div bind:this={headingCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div>
                <div class="inline-block code-blue">&gt;</div>
                <div class="block code-white">
                  &nbsp;&nbsp;&nbsp;&nbsp;Welcome to the CSS Framework!
                </div>
                <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&lt;/</div>
                <div class="inline-block code-red">h2</div>
                <div class="inline-block code-blue">&gt;</div>
              </div>
              
              <div class="whitespace-no-wrap">
                <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&lt;</div>
                <div class="inline-block code-red">div</div>
                <div bind:this={subheadingContainerClass} class="inline-block">
                  <div class="hidden">&nbsp;</div><!--
                  --><div class="hidden code-yellow">c</div><!--
                  --><div class="hidden code-yellow">l</div><!--
                  --><div class="hidden code-yellow">a</div><!--
                  --><div class="hidden code-yellow">s</div><!--
                  --><div class="hidden code-yellow">s</div><!--
                  --><div class="hidden code-blue">=</div><!--
                  --><div class="hidden code-blue">&quot;</div><!--
                  --><div class="hidden code-green">m</div><!--
                  --><div class="hidden code-green">y</div><!--
                  --><div class="hidden code-green">-</div><!--
                  --><div class="hidden code-green">4</div><!--
                  --><div class="hidden code-blue">&quot;</div>
                </div>
                <div bind:this={subheadingContainerCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div>
                <div class="inline-block code-blue">&gt;</div>

                <div class="block code-white">
                  <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&nbsp;&lt;</div>
                  <div class="inline-block code-red">p</div>
                  <div bind:this={subheadingClass} class="inline-block">
                    <div class="hidden">&nbsp;</div><!--
                    --><div class="hidden code-yellow">c</div><!--
                    --><div class="hidden code-yellow">l</div><!--
                    --><div class="hidden code-yellow">a</div><!--
                    --><div class="hidden code-yellow">s</div><!--
                    --><div class="hidden code-yellow">s</div><!--
                    --><div class="hidden code-blue">=</div><!--
                    --><div class="hidden code-blue">&quot;</div><!--
                    --><div class="hidden code-green">h</div><!--
                    --><div class="hidden code-green">e</div><!--
                    --><div class="hidden code-green">a</div><!--
                    --><div class="hidden code-green">d</div><!--
                    --><div class="hidden code-green">i</div><!--
                    --><div class="hidden code-green">n</div><!--
                    --><div class="hidden code-green">g</div><!--
                    --><div class="hidden code-green">&nbsp;</div><!--
                    --><div class="hidden code-green">h</div><!--
                    --><div class="hidden code-green">e</div><!--
                    --><div class="hidden code-green">a</div><!--
                    --><div class="hidden code-green">d</div><!--
                    --><div class="hidden code-green">i</div><!--
                    --><div class="hidden code-green">n</div><!--
                    --><div class="hidden code-green">g</div><!--
                    --><div class="hidden code-green">-</div><!--
                    --><div class="hidden code-green">3</div><!--
                    --><div class="hidden code-blue">&quot;</div>
                  </div>
                  <div bind:this={subheadingCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div>
                  <div class="inline-block code-blue">&gt;</div>
                  <div class="block code-white">
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;A utility-based alternative to the
                  </div>


                  <div class="block code-white">
                    <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;</div>
                    <div class="inline-block code-red">a&nbsp;</div>
                    <div class="inline-block code-yellow">class</div>
                    <div class="inline-block code-blue">=&quot;</div>
                    <div class="inline-block code-green">link&quot;&nbsp;</div>
                    <div class="block">
                      <div class="inline-block code-yellow">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;href</div>
                      <div class="inline-block code-blue">=&quot;</div>
                      <div class="inline-block code-green">https://www.meshdesignsystem.com&quot;</div>
                      <div class="inline-block code-blue">&gt;</div>
                    </div>
                    <div class="inline-block code-white">
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Mesh Design System
                    </div>
                    <div class="block">
                      <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;/</div>
                      <div class="inline-block code-red">a</div>
                      <div class="inline-block code-blue">&gt;</div>
                    </div>
                  </div>




                  <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&nbsp;&lt;/</div>
                  <div class="inline-block code-red">p</div>
                  <div class="inline-block code-blue">&gt;</div>
                </div>

                <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&lt;/</div>
                <div class="inline-block code-red">div</div>
                <div class="inline-block code-blue">&gt;</div>
              </div>

              <div class="whitespace-no-wrap">
                <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&lt;</div>
                <div class="inline-block code-red">p&nbsp;</div>
                <div class="inline-block code-yellow">class</div>
                <div class="inline-block code-blue">=&quot;</div>
                <div class="inline-block code-green">my-4</div>
                <div bind:this={copyClass} class="inline-block">
                  <div class="hidden">&nbsp;</div><!--
                  --><div class="hidden code-green">c</div><!--
                  --><div class="hidden code-green">o</div><!--
                  --><div class="hidden code-green">p</div><!--
                  --><div class="hidden code-green">y</div>
                </div>
                <div bind:this={copyCursor} class="cursor inline-block border-r-2 border-focus h-4 absolute"></div>
                <div class="inline-block code-blue">&quot;&gt;</div>

                <div class="block code-white">
                  <div>
                    &nbsp;&nbsp;&nbsp;&nbsp;For those not able to use our React components,
                  </div>
                  <div>
                    &nbsp;&nbsp;&nbsp;&nbsp;this framework is a non-CSS-in-JS alternative
                  </div>
                  <div>
                    &nbsp;&nbsp;&nbsp;&nbsp;powered by Tailwind.
                  </div>
                </div>

                <div class="inline-block code-blue">&nbsp;&nbsp;&nbsp;&lt;/</div>
                <div class="inline-block code-red">p</div>
                <div class="inline-block code-blue">&gt;</div>
              </div>
            
              <div>
                <div class="inline-block code-blue">&nbsp;&nbsp;&lt;/</div>
                <div class="inline-block code-red">div</div>
                <div class="inline-block code-blue">&gt;</div>
              </div>
              <div>
                <div class="inline-block code-blue">&nbsp;&lt;/</div>
                <div class="inline-block code-red">div</div>
                <div class="inline-block code-blue">&gt;</div>
              </div>
              <div>
                <div class="inline-block code-blue">&lt;/</div>
                <div class="inline-block code-red">div</div>
                <div class="inline-block code-blue">&gt;</div>
              </div>
            </div>

            <div bind:this={demo} class="shadow-lg leading-normal self-end bg-lighter rounded relative w-full my-4 showcase-demo">
              <div class="p-5">
                <div class="flex items-end max-w-full border-box">
                  <div bind:this={demoContainer} class="w-full">
                    <div bind:this={demoContent}>
                      <h2 bind:this={demoHeading}>Welcome to the CSS Framework!</h2>
                      <div bind:this={demoSubheadingContainer}>
                        <p bind:this={demoSubheading}>A utility-based alternative to the <a class="link" href="https://www.meshdesignsystem.com">Mesh Design System</a></p>
                      </div>
                      <p bind:this={demoCopy} class="my-4">
                        For those not able to use our React components, this framework is a non-CSS-in-JS alternative powered by Tailwind.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>